import React, { useCallback, useEffect, memo, useRef, useState } from "react";
import { operationDeleteGleba, operationSaveGleba } from "~/api";
import EditControl from "~/internal/draft";
import { MapContainer, TileLayer, FeatureGroup, GeoJSON } from "react-leaflet";
import MapCache from "~/components/MapCache";
import LayerControlMap from "~/components/LayerControlMap";
import L from "leaflet";

import markerIcon from "leaflet/dist/images/marker-icon.png";
import "react-leaflet-fullscreen/dist";
import "react-leaflet-fullscreen/dist/styles.css";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";

import "./styles.scss";
import mapHelpers from '../../../../../../../utils/mapHelpers';

const MAP_ZOOM = 15;

L.Marker.prototype.setIcon(
  L.icon({
    iconUrl: markerIcon
  })
);

const DrawableMap = memo(
  ({
    areaInfo,
    layers,
    onChange,
    trackId,
    operationId,
    glebaList,
    setError,
    isEditing = false,
    ...props
  }) => {
    const mapRef = useRef(null);
    const featureGroupRef = useRef(null);

    const [areasVisible, setAreasVisible] = useState(false);

    const geojson = areaInfo[0].geo_json;
    const coordinates = geojson.coordinates;

    const isMultiPolygon = geojson.type === 'MultiPolygon';

    const onlyXY = mapHelpers.getOnlyXY(coordinates, { isMultiPolygon })

    const [, updateState] = useState();
    const forceUpdate = useCallback(() => updateState({}), []);

    useEffect(() => {
      const bounds = mapHelpers.reverseCoordinates(onlyXY, { isMultiPolygon })

      if (!mapRef.current) {
        forceUpdate();
        return;
      }

      mapRef.current.fitBounds(bounds, {});
      mapRef.current.setMaxBounds(bounds);
    }, [forceUpdate, onlyXY, isMultiPolygon]);

    useEffect(() => {
      if (!featureGroupRef.current && !areasVisible) return;

      featureGroupRef.current.clearLayers();

      const features = glebaList.map(gleba => {
        const feature = {
          geometry: gleba.geo_json,
          properties: {
            id: gleba.id,
            area_ha: gleba.area_ha
          },
          type: "Feature"
        };

        return feature;
      });

      const geojson = L.geoJSON({
        type: "FeatureCollection",
        features
      });

      geojson.eachLayer(layer => {
        featureGroupRef.current.addLayer(layer);
      });
    }, [glebaList, areasVisible]);

    const handleEditArea = async () => {
      if (!featureGroupRef.current) return;

      const geojson = featureGroupRef.current.toGeoJSON();

      try {
        await saveAreas(geojson);
        onChange();
      } catch (err) {
        console.log(err);
      }
    };

    const handleDeleteArea = async area => {
      if (!featureGroupRef.current) return;

      const layers = area.layers;
      layers.eachLayer(layer => {
        const properties = layer.feature.properties;
        operationDeleteGleba(properties.id);
      });

      onChange();
    };

    const handleCreateArea = async area => {
      if (!featureGroupRef.current) return;

      const layer = area.layer;
      featureGroupRef.current.addLayer(layer);

      const geojson = featureGroupRef.current.toGeoJSON();

      try {
        const response = await saveAreas(geojson);

        if (!!response.data.message) {
          setError(response.data.message);
        }

        onChange();
      } catch (err) {
        console.log(err);
      }
    };

    const saveAreas = async geojson => {
      const saveResponse = await operationSaveGleba({
        b3PropostaId: operationId,
        geojson,
        trackId
      });

      const leafletGeoJson = L.geoJSON(saveResponse.data.result);

      featureGroupRef.current.clearLayers();
      leafletGeoJson.eachLayer(layer => {
        featureGroupRef.current.addLayer(layer);
      });

      return saveResponse;
    };

    return (
      <>
        <MapContainer ref={mapRef} fullscreenControl zoom={MAP_ZOOM} {...props}>
          <TileLayer
            // attribution='&amp;copy <a href="https://www.openstreetmap.org/about">OpenStreetMap</a>'
            // url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            url="http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}"
            minZoom="10"
            subdomains={["mt0", "mt1", "mt2", "mt3"]}
          />

          <FeatureGroup
            ref={el => {
              featureGroupRef.current = el;
              setAreasVisible(true);
            }}
          >
            <EditControl
              edit={{
                edit: isEditing ? {} : false,
                remove: isEditing ? {} : false
              }}
              draw={{
                polygon: isEditing,
                circle: isEditing,
                rectangle: isEditing,
                polyline: false,
                marker: false,
                circlemarker: false
              }}
              onEdited={handleEditArea}
              onDeleted={handleDeleteArea}
              onCreated={handleCreateArea}
            />
          </FeatureGroup>

          <LayerControlMap layers={layers} />

          <MapCache />
        </MapContainer>
      </>
    );
  }
);

export default DrawableMap;
